import { useState, useMemo } from 'react';
import { Card, Collapse, Tag, Empty, Badge, Button, Dropdown } from 'antd';
import {
    ClockCircleOutlined,
    SunOutlined,
    MoonOutlined,
    AlertOutlined,
    CalendarOutlined,
    HeartOutlined,
    ExperimentOutlined,
    PrinterOutlined, FileTextOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import PrintTemplate from '../../../components/Print/PrintTemplate';
import { printElement, openPrintPreview } from '../../../utils/printUtils';
import moment from 'moment';

const { Panel } = Collapse;

/**
 * DoctorRoundHistory Component
 * Displays doctor round data organized by date and round type (morning, evening, emergency)
 */
const DoctorRoundHistory = ({ doctorRoundForm }) => {
    const [activeKey, setActiveKey] = useState([]);
    const { CcPaperDetail } = useSelector(state => state.patient);

    // Prepare data for PrintTemplate
    const printData = useMemo(() => {
        if (!doctorRoundForm || typeof doctorRoundForm !== 'object') {
            return { formData: {}, sections: [] };
        }

        const formData = {
            patientName: CcPaperDetail?.name || 'N/A',
            uhid: CcPaperDetail?.registrationNo || 'N/A',
            ageGender: `${CcPaperDetail?.age || 'N/A'} / ${CcPaperDetail?.gender || 'N/A'}`,
        };

        const sections = [
            {
                sectionIndex: 1,
                sectionTitle: 'Patient Information',
                layout: 'horizontal',
                fields: [
                    { fieldKey: 'patientName', label: 'Patient Name' },
                    { fieldKey: 'uhid', label: 'UHID' },
                    { fieldKey: 'ageGender', label: 'Age / Gender' },
                ]
            }
        ];



        // Group rounds by date (Newest first)
        const sortedDates = Object.keys(doctorRoundForm).sort((a, b) =>
            moment(b, 'YYYY-MM-DD').valueOf() - moment(a, 'YYYY-MM-DD').valueOf()
        );

        sortedDates.forEach((date, index) => {
            const rounds = doctorRoundForm[date];
            const formattedDate = moment(date, 'YYYY-MM-DD').format('DD MMM YYYY');

            const dateSectionKey = `date_${date}`;
            const fields = [];

            // For each round type in this date
            Object.keys(rounds).forEach(type => {
                const roundData = rounds[type];
                const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
                const roundKey = `${dateSectionKey}_${type}`;

                // Categories for clear organization
                const findings = [];

                // 1. Vital Signs Summary
                const vitalList = [];
                if (roundData.temperature) vitalList.push(`Temp: ${roundData.temperature}°F`);
                if (roundData.pulse) vitalList.push(`Pulse: ${roundData.pulse}bpm`);
                if (roundData.spO2) vitalList.push(`SpO2: ${roundData.spO2}%`);
                if (roundData.respiratoryRate) vitalList.push(`RR: ${roundData.respiratoryRate}/min`);
                if (vitalList.length > 0) findings.push(`• VITALS: ${vitalList.join(' | ')}`);

                // 2. BP Summary
                const bps = [];
                if (roundData.BPMorning) bps.push(`M: ${roundData.BPMorning}`);
                if (roundData.BPEvening) bps.push(`E: ${roundData.BPEvening}`);
                if (bps.length > 0) findings.push(`• BLOOD PRESSURE: ${bps.join(' , ')}`);

                // 3. Sugar Levels Summary
                const sugars = [];
                if (roundData.fastingSugarLevel) sugars.push(`Fasting: ${roundData.fastingSugarLevel}`);
                if (roundData.PPSugarLevel) sugars.push(`PP: ${roundData.PPSugarLevel}`);
                if (roundData.randomSugarLevel) sugars.push(`Random: ${roundData.randomSugarLevel}`);
                if (sugars.length > 0) findings.push(`• SUGAR (mg/dL): ${sugars.join(' | ')}`);

                // 4. Clinical Observations (Physical)
                const phyObs = [];
                const addObs = (val, label) => {
                    if (val && val.length > 0) {
                        phyObs.push(`${label}: ${Array.isArray(val) ? val.join(', ') : val}`);
                    }
                };

                addObs(roundData.naad, 'Naad');
                addObs(roundData.yakrutNada, 'Yakrut');
                addObs(roundData.plihaNada, 'Pliha');
                addObs(roundData.jivha, 'Jivha');
                addObs(roundData.Mala, 'Mala');
                addObs(roundData.Mutra, 'Mutra');

                if (phyObs.length > 0) findings.push(`• PHYSICAL: ${phyObs.join(' | ')}`);

                // 5. Sparsha Sahatva
                if (roundData.sparshaSahatva && roundData.sparshaSahatva.length > 0) {
                    const sparshaStr = roundData.sparshaSahatva.map(item => {
                        const [k, v] = Object.entries(item)[0];
                        return `${k}(${v.join(',')})`;
                    }).join(', ');
                    findings.push(`• PALPATION: ${sparshaStr}`);
                }

                if (findings.length > 0) {
                    formData[roundKey] = findings.join('\n\n');
                    fields.push({
                        fieldKey: roundKey,
                        label: `${typeLabel} Round Observation`,
                        layout: 'vertical',
                        gridClass: 'col-12',
                        showInPrint: true
                    });
                }
            });

            if (fields.length > 0) {
                sections.push({
                    sectionIndex: sections.length + 1,
                    sectionTitle: `Date: ${formattedDate}`,
                    layout: 'grid',
                    fields
                });
            }
        });

        return { formData, sections };
    }, [doctorRoundForm, CcPaperDetail]);

    // Process and organize the doctor round data
    const organizedData = useMemo(() => {
        if (!doctorRoundForm || typeof doctorRoundForm !== 'object') {
            return [];
        }

        // Convert object to array and sort by date (newest first)
        const dates = Object.keys(doctorRoundForm).sort((a, b) =>
            moment(b, 'YYYY-MM-DD').valueOf() - moment(a, 'YYYY-MM-DD').valueOf()
        );

        return dates.map(date => ({
            date,
            rounds: doctorRoundForm[date],
            formattedDate: moment(date, 'YYYY-MM-DD').format('DD MMM YYYY'),
            isToday: moment(date, 'YYYY-MM-DD').isSame(moment(), 'day'),
            dayOfWeek: moment(date, 'YYYY-MM-DD').format('dddd')
        }));
    }, [doctorRoundForm]);

    // Get round type configuration with subtle colors
    const getRoundConfig = (type) => {
        const configs = {
            morning: {
                icon: <SunOutlined />,
                color: '#64748b',
                label: 'Morning Round',
                bgColor: '#f8fafc'
            },
            evening: {
                icon: <MoonOutlined />,
                color: '#64748b',
                label: 'Evening Round',
                bgColor: '#f1f5f9'
            },
            emergency: {
                icon: <AlertOutlined />,
                color: '#dc2626',
                label: 'Emergency Round',
                bgColor: '#fef2f2'
            }
        };
        return configs[type] || configs.morning;
    };

    // Render vital signs section
    const renderVitalSigns = (roundData) => {
        const vitals = [
            { label: 'Temperature', value: roundData.temperature, unit: '°F', icon: '🌡️' },
            { label: 'SpO2', value: roundData.spO2, unit: '%', icon: '💨' },
            { label: 'Pulse', value: roundData.pulse, unit: 'bpm', icon: '💓' },
            { label: 'Respiratory Rate', value: roundData.respiratoryRate, unit: '/min', icon: '🫁' },
            { label: 'BP Morning', value: roundData.BPMorning, unit: 'mmHg', icon: '🩺' },
            { label: 'BP Evening', value: roundData.BPEvening, unit: 'mmHg', icon: '🩺' },
        ].filter(vital => vital.value);

        if (vitals.length === 0) return null;

        return (
            <div style={{ marginBottom: '20px' }}>
                <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <HeartOutlined style={{ color: '#64748b' }} />
                    Vital Signs
                </h4>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: '12px'
                }}>
                    {vitals.map((vital, idx) => (
                        <div key={idx} style={{
                            padding: '12px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{
                                fontSize: '11px',
                                color: '#6b7280',
                                marginBottom: '4px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                {vital.icon} {vital.label}
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#1f2937'
                            }}>
                                {vital.value} <span style={{ fontSize: '12px', color: '#6b7280' }}>{vital.unit}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Render sugar levels section
    const renderSugarLevels = (roundData) => {
        const sugarLevels = [
            { label: 'Fasting', value: roundData.fastingSugarLevel },
            { label: 'PP', value: roundData.PPSugarLevel },
            { label: 'Random', value: roundData.randomSugarLevel },
        ].filter(level => level.value);

        if (sugarLevels.length === 0) return null;

        return (
            <div style={{ marginBottom: '20px' }}>
                <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <ExperimentOutlined style={{ color: '#64748b' }} />
                    Sugar Levels
                </h4>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: '12px'
                }}>
                    {sugarLevels.map((level, idx) => (
                        <div key={idx} style={{
                            padding: '12px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{
                                fontSize: '11px',
                                color: '#6b7280',
                                marginBottom: '4px',
                                fontWeight: '600'
                            }}>
                                {level.label}
                            </div>
                            <div style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#1f2937'
                            }}>
                                {level.value} <span style={{ fontSize: '12px', color: '#6b7280' }}>mg/dL</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Render array data as tags with subtle colors
    const renderArrayData = (label, data) => {
        if (!data || data.length === 0) return null;

        return (
            <div style={{ marginBottom: '16px' }}>
                <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {label}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {data.map((item, idx) => (
                        <Tag key={idx} style={{
                            margin: 0,
                            fontSize: '13px',
                            padding: '4px 12px',
                            background: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #cbd5e1'
                        }}>
                            {item}
                        </Tag>
                    ))}
                </div>
            </div>
        );
    };

    // Render Sparsha Sahatva (complex nested array)
    const renderSparshaSahatva = (data) => {
        if (!data || data.length === 0) return null;

        return (
            <div style={{ marginBottom: '16px' }}>
                <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    Sparsha Sahatva (Palpation Findings)
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '12px'
                }}>
                    {data.map((item, idx) => {
                        const [key, values] = Object.entries(item)[0];
                        if (!values || values.length === 0) return null;

                        return (
                            <div key={idx} style={{
                                padding: '12px',
                                background: '#f9fafb',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div style={{
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '8px'
                                }}>
                                    {key}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {values.map((val, vIdx) => (
                                        <Tag key={vIdx} style={{
                                            margin: 0,
                                            fontSize: '11px',
                                            background: '#e0e7ff',
                                            color: '#4338ca',
                                            border: '1px solid #c7d2fe'
                                        }}>
                                            {val}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Render individual round data
    const renderRoundData = (roundType, roundData) => {
        if (!roundData) return null;

        const config = getRoundConfig(roundType);

        return (
            <div style={{
                marginBottom: '16px',
                padding: '20px',
                background: config.bgColor,
                borderRadius: '8px',
                border: `1px solid ${config.color}20`
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        color: config.color
                    }}>
                        {config.icon}
                    </div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: '600',
                        color: config.color
                    }}>
                        {config.label}
                    </h3>
                </div>

                {renderSugarLevels(roundData)}
                {renderVitalSigns(roundData)}
                {renderArrayData('Naad (Pulse)', roundData.naad)}
                {renderArrayData('Yakrut Nada', roundData.yakrutNada)}
                {renderArrayData('Pliha Nada', roundData.plihaNada)}
                {renderSparshaSahatva(roundData.sparshaSahatva)}
                {renderArrayData('Mala', roundData.Mala)}
                {renderArrayData('Mutra', roundData.Mutra)}
                {renderArrayData('Jivha (Tongue)', roundData.jivha)}
            </div>
        );
    };

    if (!organizedData || organizedData.length === 0) {
        return (
            <Card style={{ marginTop: '20px' }}>
                <Empty
                    description="No doctor round history available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Card>
        );
    }

    return (
        <div style={{ marginTop: '24px' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <ClockCircleOutlined style={{ fontSize: '24px', color: '#2e37a4' }} />
                    <div>
                        <h2 style={{
                            margin: 0,
                            fontSize: '22px',
                            fontWeight: '600',
                            color: '#1a202c'
                        }}>
                            Doctor Round History
                        </h2>
                        <p style={{
                            margin: 0,
                            fontSize: '14px',
                            color: '#6b7280'
                        }}>
                            Complete medical examination records organized by date
                        </p>
                    </div>
                </div>

                <div className="no-print">
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: 'print',
                                    label: 'Print History',
                                    icon: <PrinterOutlined />,
                                    onClick: () => printElement('doctor-round-print-area', 'Doctor Round History'),
                                },
                                {
                                    key: 'preview',
                                    label: 'Print Preview',
                                    icon: <FileTextOutlined />,
                                    onClick: () => openPrintPreview('doctor-round-print-area', 'Doctor Round History - Preview'),
                                },
                            ],
                        }}
                        placement="bottomRight"
                    >
                        <Button type="primary" icon={<PrinterOutlined />}>
                            Print Options
                        </Button>
                    </Dropdown>
                </div>
            </div>

            <Collapse
                activeKey={activeKey}
                onChange={setActiveKey}
                accordion={false}
                style={{
                    background: 'transparent',
                    border: 'none'
                }}
            >
                {organizedData.map((dateEntry, idx) => {
                    const roundTypes = Object.keys(dateEntry.rounds);
                    const roundCount = roundTypes.length;

                    return (
                        <Panel
                            key={dateEntry.date}
                            header={
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    paddingRight: '16px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <CalendarOutlined style={{ fontSize: '20px', color: '#2e37a4' }} />
                                        <div>
                                            <div style={{
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                color: '#1f2937'
                                            }}>
                                                {dateEntry.formattedDate}
                                                {dateEntry.isToday && (
                                                    <Tag color="green" style={{ marginLeft: '8px' }}>Today</Tag>
                                                )}
                                            </div>
                                            <div style={{
                                                fontSize: '13px',
                                                color: '#6b7280',
                                                marginTop: '2px'
                                            }}>
                                                {dateEntry.dayOfWeek}
                                            </div>
                                        </div>
                                    </div>
                                    <Badge count={roundCount} style={{ backgroundColor: '#2e37a4' }} />
                                </div>
                            }
                            style={{
                                marginBottom: '16px',
                                background: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                overflow: 'hidden'
                            }}
                        >
                            <div style={{ padding: '8px' }}>
                                {roundTypes.map(roundType =>
                                    renderRoundData(roundType, dateEntry.rounds[roundType])
                                )}
                            </div>
                        </Panel>
                    );
                })}
            </Collapse>

            {/* Print Template - Hidden on screen, visible in print */}
            <div style={{ display: 'none' }}>
                <div id="doctor-round-print-area">
                    <PrintTemplate
                        formData={printData.formData}
                        sections={printData.sections}
                        formTitle="Doctor Round Examination History"
                    />
                </div>
            </div>
        </div>
    );
};

export default DoctorRoundHistory;
